/**
 * Total Connect email UI preferences (combined inbox, compact view, reply attachments, etc.)
 * persisted on TotalAccessOfflineDB via `emailUiCacheStore` opaque meta — not localStorage.
 *
 * Legacy keys `emailPreferences` and `emailTheme` are migrated on first hydrate per client.
 */
import { emailUiCacheStore } from './emailUiCacheStore';

export const TOTAL_CONNECT_EMAIL_PREFS_UPDATED_EVENT = 'totalConnectEmailPrefsUpdated';

const META_SEGMENT = 'connect_ui_prefs';

const LEGACY_LS_PREFS = 'emailPreferences';
const LEGACY_LS_THEME = 'emailTheme';

export type EmailConnectUiPrefs = {
  threadedView: boolean;
  compactView: boolean;
  showEmailNotifications: boolean;
  includeAttachmentsOnReply: boolean;
  combinedInbox: boolean;
  showPreview: boolean;
};

export const DEFAULT_EMAIL_CONNECT_UI_PREFS: EmailConnectUiPrefs = {
  threadedView: true,
  compactView: false,
  showEmailNotifications: true,
  includeAttachmentsOnReply: true,
  combinedInbox: true,
  showPreview: true,
};

let snapshot: EmailConnectUiPrefs = { ...DEFAULT_EMAIL_CONNECT_UI_PREFS };

function recordToPrefs(r: Record<string, unknown>): EmailConnectUiPrefs {
  return {
    threadedView:
      typeof r.threadedView === 'boolean'
        ? r.threadedView
        : DEFAULT_EMAIL_CONNECT_UI_PREFS.threadedView,
    compactView:
      typeof r.compactView === 'boolean'
        ? r.compactView
        : DEFAULT_EMAIL_CONNECT_UI_PREFS.compactView,
    showEmailNotifications:
      typeof r.showEmailNotifications === 'boolean'
        ? r.showEmailNotifications
        : DEFAULT_EMAIL_CONNECT_UI_PREFS.showEmailNotifications,
    includeAttachmentsOnReply:
      typeof r.includeAttachmentsOnReply === 'boolean'
        ? r.includeAttachmentsOnReply
        : DEFAULT_EMAIL_CONNECT_UI_PREFS.includeAttachmentsOnReply,
    combinedInbox:
      typeof r.combinedInbox === 'boolean'
        ? r.combinedInbox
        : DEFAULT_EMAIL_CONNECT_UI_PREFS.combinedInbox,
    showPreview:
      typeof r.showPreview === 'boolean'
        ? r.showPreview
        : DEFAULT_EMAIL_CONNECT_UI_PREFS.showPreview,
  };
}

function readLegacyLocalStoragePartial(): Partial<EmailConnectUiPrefs> {
  if (typeof window === 'undefined') return {};
  const out: Partial<EmailConnectUiPrefs> = {};
  try {
    const rawPrefs = localStorage.getItem(LEGACY_LS_PREFS);
    if (rawPrefs) {
      const p = JSON.parse(rawPrefs) as Record<string, unknown>;
      if (p && typeof p === 'object') {
        if (typeof p.threadedView === 'boolean') out.threadedView = p.threadedView;
        if (typeof p.compactView === 'boolean') out.compactView = p.compactView;
        if (typeof p.showEmailNotifications === 'boolean')
          out.showEmailNotifications = p.showEmailNotifications;
        if (typeof p.includeAttachmentsOnReply === 'boolean')
          out.includeAttachmentsOnReply = p.includeAttachmentsOnReply;
        if (typeof p.combinedInbox === 'boolean') out.combinedInbox = p.combinedInbox;
        if (typeof p.showPreview === 'boolean') out.showPreview = p.showPreview;
      }
    }
    const rawTheme = localStorage.getItem(LEGACY_LS_THEME);
    if (rawTheme) {
      const t = JSON.parse(rawTheme) as Record<string, unknown>;
      if (t && typeof t === 'object' && typeof t.compactView === 'boolean') {
        out.compactView = t.compactView;
      }
    }
  } catch {
    /* ignore corrupt legacy */
  }
  return out;
}

export function clearLegacyEmailPrefsLocalStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(LEGACY_LS_PREFS);
    localStorage.removeItem(LEGACY_LS_THEME);
  } catch {
    /* ignore */
  }
}

function emitPrefsUpdated(prefs: EmailConnectUiPrefs): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<EmailConnectUiPrefs>(TOTAL_CONNECT_EMAIL_PREFS_UPDATED_EVENT, {
      detail: prefs,
    }),
  );
}

async function writeToIdb(clientId: number, prefs: EmailConnectUiPrefs): Promise<void> {
  const asRecord: Record<string, unknown> = { ...prefs };
  await emailUiCacheStore.setOpaqueMeta(clientId, META_SEGMENT, asRecord);
}

/** Synchronous read of last known prefs (hydrate first for accurate data). */
export function getEmailConnectPrefsSnapshot(): EmailConnectUiPrefs {
  return snapshot;
}

export async function hydrateEmailConnectPrefs(clientId: number): Promise<EmailConnectUiPrefs> {
  if (!clientId || typeof window === 'undefined') {
    return snapshot;
  }

  const raw = await emailUiCacheStore.getOpaqueMeta<Record<string, unknown>>(
    clientId,
    META_SEGMENT,
  );

  let next: EmailConnectUiPrefs;

  if (raw && typeof raw === 'object') {
    next = recordToPrefs(raw);
    clearLegacyEmailPrefsLocalStorage();
  } else {
    const legacy = readLegacyLocalStoragePartial();
    next = { ...DEFAULT_EMAIL_CONNECT_UI_PREFS, ...legacy };
    await writeToIdb(clientId, next);
    clearLegacyEmailPrefsLocalStorage();
  }

  snapshot = next;
  return snapshot;
}

export type MergePersistEmailConnectPrefsOptions = {
  /** When false, updates IDB + snapshot only (no window event). Default true. */
  emit?: boolean;
};

/**
 * Merge `patch` onto the current snapshot, persist to offline DB, optionally broadcast.
 */
export async function mergeAndPersistEmailConnectPrefs(
  clientId: number,
  patch: Partial<EmailConnectUiPrefs>,
  options?: MergePersistEmailConnectPrefsOptions,
): Promise<EmailConnectUiPrefs> {
  if (!clientId || typeof window === 'undefined') {
    return snapshot;
  }

  const merged: EmailConnectUiPrefs = { ...snapshot, ...patch };
  await writeToIdb(clientId, merged);
  snapshot = merged;
  clearLegacyEmailPrefsLocalStorage();
  if (options?.emit !== false) {
    emitPrefsUpdated(merged);
  }
  return merged;
}
